import get from 'lodash/get';
import type { SVGProps } from 'react';
import { useFormContext } from 'react-hook-form';
import type { GlobalError } from 'react-hook-form/dist/types/errors';
import { useIntl } from 'react-intl';

import type { InputProps } from '@/components/Input';
import { Input } from '@/components/Input';
import { InputErrorMessage } from '@/components/InputErrorMessage';
import { getPlaceholderSuffix } from '@/lib/forms/helpers';

import * as S from './InputField.styles';

export interface InputFieldProps extends InputProps {
  icon?: React.ComponentType<SVGProps<SVGSVGElement>>;
  name: string;
}

export const InputField = ({ placeholder, ...props }: InputFieldProps) => {
  const { formatMessage } = useIntl();
  const { clearErrors, formState, register } = useFormContext();

  const label = getPlaceholderSuffix(
    placeholder,
    props.required,
    formatMessage,
  );
  const name = props.name;
  const error = get(formState.errors, name) as GlobalError;

  // react-hook-form should always return a registration object, but in unit-test
  // scenarios we sometimes provide a mocked implementation that may return
  // `undefined`. Safely handle that possibility to avoid runtime errors.
  const registeredResult = register(name) as
    | { onChange?: (...args: any[]) => void }
    | undefined;

  const registerOnChange = registeredResult?.onChange;
  // Spread only when we have a valid registration object – otherwise fall back
  // to an empty object to keep JSX spread safe.
  const registeredProps = registeredResult ?? {};

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    if (typeof clearErrors === 'function') {
      clearErrors(name);
    }

    if (typeof registerOnChange === 'function') {
      registerOnChange(event);
    }

    if (typeof props.onChange === 'function') {
      props.onChange(event);
    }
  };

  return (
    <S.Wrapper data-test-id='InputFieldWrapper'>
      {props.icon && (
        <S.IconContainer
          as={props.icon}
          data-test-id='InputFieldIconContainer'
        />
      )}
      <Input
        hasError={!!error}
        label={label}
        {...registeredProps}
        onChange={handleChange}
        {...props}
        data-test-id='InputFieldInput'
        placeholder=' '
      />
      <InputErrorMessage data-test-id='InputFieldErrorMessage' error={error} />
    </S.Wrapper>
  );
};

InputField.displayName = 'InputField';
